import { $applySmart } from '../../../../helpers/digest/digest';

export const DIRECTIVE_NAME = 'updLayout';

/**
 *  Allows defining a custom layout algorithm for the children of an element.
 *  The layout algorithm will run whenever changes are detected to the element's
 *  dimensions.
 *
 *  Usage:
 *  1. Create a function that applies a layout algorithm to en elements children.
 *  The function is passed two arguments: an element's direct children and the
 *  element itself.
 *
 *  ```javascript
 *  export const myCustomLayout = (elements, container) => {
 *    elements.forEach(el => el.style.cssText(`margin-bottom: ${el.offsetHeight}`));
 *  }
 *  ```
 *
 *  2. Register the layout function with the `updLayoutsProvider`.
 *
 *  ```javascript
 *    angular
 *      .module('myLayoutModule', [LayoutModule.name])
 *      .config(updLayoutsProvider => {
 *        updLayoutsProvider.register('myCustomLayout', myCustomLayout);
 *      });
 *  ```
 *
 *  3. Apply the layout to the desired element:
 *
 *  ```html
 *  <div upd-layout="myCustomLayout">
 *    <div></div>
 *    ...
 *  </div>
 *  ```
 */
export function layoutDirective(updLayouts) {
  'ngInject';
  return {
    restrict: 'A',
    link: ($scope, $element, $attr) => {
      const el = $element[0];
      const layoutName = $attr[DIRECTIVE_NAME];
      const layout = updLayouts.get(layoutName);
      const applySmart = $applySmart($scope);
      let containerDimensions = dimensionsString(el);

      // Check if the directive's element's size has changed when the window resizes.
      window.addEventListener('resize', handleResize);
      $scope.$on('$destroy', () => {
        window.removeEventListener('resize', handleResize);
      });

      // Watch the directive's element for changes in width/height.
      $scope.$watch(() => dimensionsString(el), applyLayout);

      function applyLayout() {
        layout(Array.from(el.children), el);
      }

      function handleResize() {
        const newContainerDimensions = dimensionsString(el);

        if (
          containerDimensions !== newContainerDimensions &&
          newContainerDimensions.indexOf('NaN') === -1
        ) {
          containerDimensions = newContainerDimensions;
          applySmart(applyLayout);
        }
      }
    },
  };
}

function dimensionsString(el) {
  return `${el.offsetWidth}x${el.offsetHeight}`;
}
